import PropTypes from 'prop-types';
import { useRef, useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import * as Cesium from 'cesium';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Custom hooks and utilities
import { useCesiumViewer } from './hooks/useCesiumViewer';
import { useMeasurement } from './hooks/useMeasurement';
import { useViewpoints } from './hooks/useViewpoints';
import { useAnnotations } from './hooks/useAnnotations';
import { useProfile } from './hooks/useProfile';
import { useViewerPanels } from './hooks/useViewerPanels';
import { useViewerSettings } from './hooks/useViewerSettings';
import { useTransformationTool } from './hooks/useTransformationTool';
import { decodeTransform } from './utils/transformationCodec';
import axios from 'axios';
import config from '../config.js';
import { useMainPageTip } from './hooks/useTips';
import TipsWindow from './components/TipsWindow';
import { AnnotationInputDialog } from './components/AnnotationInputDialog';
import { SplitScreenDivider } from './components/SplitScreenDivider';
import { FloatingProfilePreview } from './components/FloatingProfilePreview';
import MinimapViewer from './components/MinimapViewer';
import SmartZoomController from './utils/SmartZoomController';
import { useTreePopups } from './hooks/useTreePopups';
import ColorLegend from './components/ColorLegend';
import { useWalkMode } from './hooks/useWalkMode';
import { ViewerLoadingScreen } from './components/ViewerLoadingScreen';

// New UI components
import ViewerToolbar from './components/ViewerToolbar';
import NavigationCluster from './components/NavigationCluster';
import { StatusBar } from './components/StatusBar';
import ToolPanel from './components/ToolPanel';

// Tool Components
import MeasurementTools from './tools/MeasurementTools';
import GaussianSplattingTools from './tools/GaussianSplattingTools';
import ModelsTools from './tools/ModelsTools';
import ViewpointsTools from './tools/ViewpointsTools';
import AnnotationsTools from './tools/AnnotationsTools';
import LayersTools from './tools/LayersTools';
import SettingsTools from './tools/SettingsTools';
import ClippingPlaneTools from './tools/ClippingPlaneTools';
import ProfilerTools from './tools/ProfilerTools';
import TransformationTools from './tools/TransformationTools';
import { createCylinderEditController } from './utils/cylinderEditController';
import TreePopups from './popups/TreePopups';
import { ViewerSettingsProvider } from './context/ViewerSettingsContext';

import 'cesium/Build/Cesium/Widgets/widgets.css';

// Constants
const DRAWER_WIDTH = 285;


const VISUAL_FIDELITY_PRESETS = [
  { // Quality
    maximumScreenSpaceError: 8,
    maximumCacheOverflowBytes: 1024 * 1024 * 1024,
    dynamicScreenSpaceError: false,
    dynamicScreenSpaceErrorFactor: 4.0,
    foveatedScreenSpaceError: false,
    foveatedConeSize: 1.0,
    foveatedTimeDelay: 0.0,
    skipLevelOfDetail: false,
    progressiveResolutionHeightFraction: 0.0,
    cullRequestsWhileMovingMultiplier: 20.0,
    preloadFlightDestinations: true,
    shadows: 0
  },
  { // Balanced
    maximumScreenSpaceError: 16,
    maximumCacheOverflowBytes: 512 * 1024 * 1024,
    dynamicScreenSpaceError: true,
    dynamicScreenSpaceErrorFactor: 24.0,
    foveatedScreenSpaceError: true,
    foveatedConeSize: 0.3,
    foveatedTimeDelay: 0.2,
    skipLevelOfDetail: false,
    progressiveResolutionHeightFraction: 0.3,
    cullRequestsWhileMovingMultiplier: 60.0,
    preloadFlightDestinations: true,
    shadows: 0
  },
  { // Performance
    maximumScreenSpaceError: 24,
    maximumCacheOverflowBytes: 256 * 1024 * 1024,
    dynamicScreenSpaceError: true,
    dynamicScreenSpaceErrorFactor: 48.0,
    foveatedScreenSpaceError: true,
    foveatedConeSize: 0.1,
    foveatedTimeDelay: 0.5,
    skipLevelOfDetail: true,
    progressiveResolutionHeightFraction: 0.5,
    cullRequestsWhileMovingMultiplier: 120.0,
    preloadFlightDestinations: false,
    shadows: 0
  },
];

/**
 * Base Cesium Viewer Component
 * Shared logic and UI between CesiumViewer and PublicCesiumViewer
 */
function BaseCesiumViewer({
  id,
  token,
  gaussianSplattingId,
  resultsAssetId,
  transformationPointsId: initialTransformationPointsId = null,
  transformationPointsStr = null,
  transformationGsId: initialTransformationGsId = null,
  transformationGsStr = null,
  isPublic = false,
}) {
  const containerRef = useRef(null);
  const hasPointCloud = Boolean(id);
  const hasGaussianOnly = !hasPointCloud && Boolean(gaussianSplattingId);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { adminMode, jwt } = useContext(AuthContext);

  // Main page tip
  const { isOpen: isMainTipOpen, title: mainTipTitle, markdownBody: mainTipBody, close: closeMainTip } = useMainPageTip();
  
  // Visualization, terrain, camera, and world settings (from useViewerSettings hook)
  const {
    pointSize, setPointSize,
    useBrowserRecommendationResolution, setUseBrowserRecommendationResolution,
    eyeDomeLighting, setEyeDomeLighting,
    eyeDomeLightingStrength, setEyeDomeLightingStrength,
    eyeDomeLightingRadius, setEyeDomeLightingRadius,
    globeLighting, setGlobeLighting,
    atmosphereRendering, setAtmosphereRendering,
    terrainRendering, setTerrainRendering,
    contourLinesEnabled, setContourLinesEnabled,
    contourSpacing, setContourSpacing,
    thickLineInterval, setThickLineInterval,
    lineThickness, setLineThickness,
    fieldOfView, setFieldOfView,
    zoomFactor, setZoomFactor,
    visualFidelity, setVisualFidelity,
    ssaoEnabled, setSSAOEnabled,
    ssaoIntensity, setSSAOIntensity,
    fxaaEnabled, setFXAAEnabled,
    saveSettings,
  } = useViewerSettings();

  // Panel states (from useViewerPanels hook)
  const {
    measurementPanelOpen, setMeasurementPanelOpen,
    gaussianPanelOpen, setGaussianPanelOpen,
    modelsPanel3DOpen,
    viewpointsPanelOpen,
    annotationsPanelOpen,
    settingsPanelOpen,
    baseLayerPanelOpen,
    clippingPlanePanelOpen, setClippingPlanePanelOpen,
    profilerPanelOpen, setProfilerPanelOpen,
    transformationPanelOpen, toggleTransformationPanel,
    profileLoadingId, setProfileLoadingId,
    closeAllPanels,
    closePointCloudPanels,
    togglePanel,
    toggleMeasurementPanel,
    toggleGaussianPanel,
    toggle3DModelsPanel,
    toggleViewpointsPanel,
    toggleAnnotationsPanel,
    toggleSettingsPanel,
    toggleBaseLayerPanel,
    toggleClippingPlanePanel,
    toggleProfilerPanel,
  } = useViewerPanels({
    onSettingsClose: saveSettings,
    onMeasurementClose: () => clearMeasurementHighlightRef.current?.(),
    setMode: (mode) => setModeRef.current?.(mode),
    modeNone: 'none',
  });

  // Smart zoom controller
  const smartZoomControllerRef = useRef(null);
  const clearMeasurementHighlightRef = useRef(null);
  // Used to forward setMode into useViewerPanels without reordering hook calls
  const setModeRef = useRef(null);

  // Hide transformation tool when any result asset is a GeoJSON (results-view mode)
  const [hasGeoJsonResult, setHasGeoJsonResult] = useState(false);
  const handleAssetTypesResolved = useCallback((types) => {
    setHasGeoJsonResult(Object.values(types).includes('GEOJSON'));
  }, []);

  // Minimap toggle state
  const [minimapOpen, setMinimapOpen] = useState(false);
  const handleToggleMinimap = useCallback(() => setMinimapOpen(prev => !prev), []);

  // Profile currently shown in the floating preview window (null = closed)
  const [previewProfile, setPreviewProfile] = useState(null);
  const cylinderEditControllerRef = useRef(null);

  const { clearProfiles } = useProfile();
  const clearProfilesRef = useRef(clearProfiles);
  useEffect(() => {
    clearProfilesRef.current = clearProfiles;
  }, [clearProfiles]);
  const datasetIdRef = useRef(null);
  useEffect(() => {
    const nextId = id ?? null;
    if (datasetIdRef.current !== nextId) {
      try { clearProfilesRef.current?.(); } catch (error) {
        console.warn('Failed to clear profiles on dataset change:', error);
      }
      datasetIdRef.current = nextId;
    }
  }, [id]);

  useEffect(() => () => {
    try { clearProfilesRef.current?.(); } catch (error) {
      console.warn('Failed to clear profiles on viewer unmount:', error);
    }
    datasetIdRef.current = null;
  }, []);

  // Settings are now loaded automatically by useViewerSettings hook on mount

  // Initialize Cesium viewer with custom hooks
  const { 
    viewer, 
    viewerError,
    tileset,
    gaussianTileset,
    currentDisplayMode,
    currentViewMode,
    splitPosition,
    switchDisplayMode,
    switchViewMode,
    updateSplitPosition,
    updatePointSize, 
    updateUseBrowserRecommendationResolution,
    updateEyeDomeLighting, 
    updateEyeDomeLightingStrength,
    updateEyeDomeLightingRadius,
    updateGlobeLighting,
    updateAtmosphereRendering,
    updateTerrainRendering,
    updateContourLines,
    setupClickHandler,
    // Camera functions
    recenterCamera,
    updateCameraFOV,
    updateCameraMovement,
    // Property visualization functions
    selectedProperty,
    classificationFilter,
    updateClassificationFilter,
    intensityRange,
    elevationRange,
    distanceRange,
    distanceBounds,
    elevationBounds,
    updateSelectedProperty,
    updateIntensityRange,
    updateElevationRange,
    recalibrateElevation,
    updateDistanceRange,
    intensityColormap,
    updateIntensityColormap,
    elevationColormap,
    updateElevationColormap,
    distanceColormap,
    updateDistanceColormap,
    // BaseLayer data for custom picker
    imageryProviderViewModels,
    terrainProviderViewModels,
    updateProfilerHighlighting,
    updateSSAO,
    updateFXAA,
  } = useCesiumViewer(containerRef, token, id, gaussianSplattingId);

  // Tree popup state and interactions
  const {
    treePopups,
    trackedPopupId,
    editingPopupId,
    setEditingPopupId,
    treePopupsRef,
    closeTreePopup,
    deleteTreePopupAndEntity,
    toggleTrackedPopup,
    togglePopupEditMode,
    handleEditFieldChange,
    handleRadiusStep,
    handleTreeEntityClick,
    updateTreePopupScreens,
    getEntityByPopupId,
    computeAxisFromOrientation,
    applyPopupUpdates,
    treePopupViewModels,
  } = useTreePopups(viewer);

  // Decode saved transformation strings into initial values for each tileset
  const initialPointValues = useMemo(
    () => transformationPointsStr ? decodeTransform(transformationPointsStr) : null,
    [transformationPointsStr],
  );
  const initialGsValues = useMemo(
    () => transformationGsStr ? decodeTransform(transformationGsStr) : null,
    [transformationGsStr],
  );

  // Transformation tool — state lives here so it persists across panel open/close
  const {
    pointValues: transformPointValues,
    gsValues: transformGsValues,
    pointValuesRef: transformPointValuesRef,
    gsValuesRef: transformGsValuesRef,
    pointGeoRef: transformPointGeoRef,
    applyPoint: transformApplyPoint,
    applyGs: transformApplyGs,
  } = useTransformationTool(tileset, gaussianTileset, initialPointValues, initialGsValues);

  // Refs for IDs — initialised from navigate state, then overwritten by the mount fetch below.
  // Using refs (not state) so saveTransformation always reads the latest value without re-renders.
  const dataIdRef                 = useRef(null);
  const transformationPointsIdRef = useRef(initialTransformationPointsId);
  const transformationGsIdRef     = useRef(initialTransformationGsId);

  // Fetch the data record from Strapi on mount to resolve the Strapi data ID
  // and any existing transformation record IDs.
  useEffect(() => {
    if (isPublic || !jwt) return;
    const assetId = id || gaussianSplattingId;
    if (!assetId) return;
    const filterField = id ? 'id_cesium' : 'id_cesium_gs';

    axios.get(`${config.apiUrl}/api/datas`, {
      headers: { Authorization: `Bearer ${jwt}` },
      params: {
        [`filters[${filterField}][$eq]`]: assetId,
        'populate[0]': 'transformation_cesium_points',
        'populate[1]': 'transformation_cesium_gs',
      },
    }).then(res => {
      const record = res.data?.data?.[0];
      if (!record) return;
      dataIdRef.current = record.id;
      const pt = record.attributes?.transformation_cesium_points?.data;
      const gs = record.attributes?.transformation_cesium_gs?.data;
      if (pt?.id) transformationPointsIdRef.current = pt.id;
      if (gs?.id) transformationGsIdRef.current = gs.id;
    }).catch(err => console.error('[BaseCesiumViewer] Failed to fetch transformation metadata:', err));
  }, [isPublic, jwt, id, gaussianSplattingId]);

  // Save current transformation values to the Strapi transformation record.
  // If the record doesn't exist yet, create it and link it to the dataset.
  const saveTransformation = useCallback(async (target) => {
    const headers = { Authorization: `Bearer ${jwt}` };

    const saveOne = async (idRef, vals, dataField) => {
      if (idRef.current) {
        // Record exists — update it
        await axios.put(
          `${config.apiUrl}/api/transformations/${idRef.current}`,
          { data: { ...vals } },
          { headers },
        );
      } else if (dataIdRef.current) {
        // Record doesn't exist — create it and link it to the dataset
        const res = await axios.post(
          `${config.apiUrl}/api/transformations`,
          { data: { ...vals } },
          { headers },
        );
        const newId = res.data.data.id;
        idRef.current = newId;
        await axios.put(
          `${config.apiUrl}/api/datas/${dataIdRef.current}`,
          { data: { [dataField]: newId } },
          { headers },
        );
      } else {
        throw new Error(`Cannot save ${dataField}: dataset record not found`);
      }
    };

    if (target === 'points' || target === 'both')
      await saveOne(transformationPointsIdRef, transformPointValuesRef.current, 'transformation_cesium_points');
    if (target === 'gs' || target === 'both')
      await saveOne(transformationGsIdRef, transformGsValuesRef.current, 'transformation_cesium_gs');
  }, [jwt, transformPointValuesRef, transformGsValuesRef]);

  // Recompute elevation coloring uniforms after every point cloud transform so
  // the height shader stays in sync with the tileset's new world position.
  // Also fires when tileset first becomes available (after the initial transform
  // from useTransformationTool has already been applied).
  useEffect(() => {
    recalibrateElevation();
  }, [transformPointValues, tileset, recalibrateElevation]);

  useEffect(() => {
    if (!hasGaussianOnly) {
      return;
    }
    if (switchViewMode && currentViewMode !== 'full') {
      switchViewMode('full');
    }
    if (switchDisplayMode && currentDisplayMode !== 'gaussian') {
      switchDisplayMode('gaussian');
    }
    console.log(currentViewMode,currentDisplayMode)
  }, [hasGaussianOnly, currentViewMode, currentDisplayMode, switchViewMode, switchDisplayMode]);

  useEffect(() => {
    if (!hasGaussianOnly) {
      return;
    }
    setMeasurementPanelOpen(false);
    setGaussianPanelOpen(false);
    setClippingPlanePanelOpen(false);
    setProfilerPanelOpen(false);
  }, [hasGaussianOnly]);

  const [datasetLoaded, setDatasetLoaded] = useState(false);

  useEffect(() => {
    if (!viewer) {
      setDatasetLoaded(false);
      return;
    }
    const targetTileset = tileset || gaussianTileset;
    if (!targetTileset) {
      // No tileset expected (edge case) — consider it loaded
      setDatasetLoaded(true);
      return;
    }
    const removeListener = targetTileset.allTilesLoaded.addEventListener(() => {
      setDatasetLoaded(true);
      removeListener();
    });
    return removeListener;
  }, [viewer, tileset, gaussianTileset]);

  useEffect(() => {
    if (viewerError) {
      // Show appropriate message based on error type
      const messageKey = viewerError.isProcessing ? 'ViewerNotReady' : 'ErrorPointCloudLoading';
      enqueueSnackbar(t(messageKey), { variant: viewerError.isProcessing ? 'warning' : 'error' });
      if (isPublic) {
        navigate('/public/thank-you');
      } else if (adminMode) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    }
  }, [viewerError, enqueueSnackbar, t, navigate, adminMode, isPublic]);

  // Asset names fetched inside ModelsTools

  // Initialize measurement functionality
  const {
    measurementMode,
    measurementPoints,
    completedMeasurements,
    highlightedMeasurement,
    setMode,
    clearMeasurements,
    deleteMeasurement,
    exportMeasurements,
    importMeasurements,
    handleMeasurementClick,
    handleMeasurementRightClick,
    highlightMeasurement,
    clearMeasurementHighlight,
    toggleMeasurementVisibility,
    MEASUREMENT_MODES: MODES
  } = useMeasurement(viewer);
  setModeRef.current = setMode; // keep ref in sync so useViewerPanels can reset mode
  // Profiler disabled

  // Initialize viewpoints functionality
  const {
    viewpoints,
    highlightedViewpoint,
    saveCurrentViewpoint,
    restoreViewpoint,
    deleteViewpoint,
    exportViewpoints,
    importViewpoints,
    clearAllViewpoints,
    showPyramids,
    hidePyramids,
    highlightPyramid,
    clearPyramidHighlight
  } = useViewpoints(viewer);

  // Initialize annotations functionality
  const {
    annotations,
    highlightedAnnotation,
    annotationMode,
    setAnnotationMode,
    addAnnotation,
    deleteAnnotation: deleteAnnotationFn,
    clearAllAnnotations,
    flyToAnnotation,
    highlightAnnotation,
    clearHighlight,
    showAnnotations,
    hideAnnotations,
    exportAnnotations,
    importAnnotations,
    getCurrentCameraState
  } = useAnnotations(viewer);


  // Sync all viewer settings whenever any setting or tileset changes
  useEffect(() => {
    updatePointSize(pointSize);
    updateUseBrowserRecommendationResolution(useBrowserRecommendationResolution);
    updateEyeDomeLighting(eyeDomeLighting);
    updateEyeDomeLightingStrength(eyeDomeLightingStrength);
    updateEyeDomeLightingRadius(eyeDomeLightingRadius);
    updateGlobeLighting(globeLighting);
    updateAtmosphereRendering(atmosphereRendering);
    updateTerrainRendering(terrainRendering);
    updateContourLines(contourLinesEnabled, contourSpacing, thickLineInterval, lineThickness);
    updateCameraFOV(fieldOfView);
    updateCameraMovement(zoomFactor);
  }, [
    tileset,
    pointSize, useBrowserRecommendationResolution,
    eyeDomeLighting, eyeDomeLightingStrength, eyeDomeLightingRadius,
    globeLighting, atmosphereRendering, terrainRendering,
    contourLinesEnabled, contourSpacing, thickLineInterval, lineThickness,
    fieldOfView, zoomFactor,
    updatePointSize, updateUseBrowserRecommendationResolution,
    updateEyeDomeLighting, updateEyeDomeLightingStrength, updateEyeDomeLightingRadius,
    updateGlobeLighting, updateAtmosphereRendering, updateTerrainRendering,
    updateContourLines, updateCameraFOV, updateCameraMovement,
  ]);

  // SSAO is scene-wide (not tileset-dependent), so it has its own effect
  useEffect(() => {
    updateSSAO(ssaoEnabled, ssaoIntensity);
  }, [viewer, ssaoEnabled, ssaoIntensity, updateSSAO]);

  // FXAA — also scene-wide
  useEffect(() => {
    updateFXAA(fxaaEnabled);
  }, [viewer, fxaaEnabled, updateFXAA]);

  // Walk mode
  const { isWalkMode, toggleWalkMode } = useWalkMode(viewer);

  // Apply visual fidelity preset to tileset
  useEffect(() => {
    if (!tileset) return;
    const preset = VISUAL_FIDELITY_PRESETS[visualFidelity] || VISUAL_FIDELITY_PRESETS[1];
    Object.assign(tileset, preset);
  }, [tileset, visualFidelity]);

  // Helper function to check if measurement should be allowed at a position
  const isMeasurementAllowedAtPosition = (screenPosition) => {
    if (!containerRef.current) return false;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const relativeX = (screenPosition.x - containerRect.left) / containerRect.width;
    
    // In full mode with Gaussian splatting, measurements are not allowed
    if (currentViewMode === 'full' && currentDisplayMode === 'gaussian') {
      return false;
    }
    
    // In split mode, measurements are only allowed on the left side (point cloud)
    if (currentViewMode === 'split' && relativeX > splitPosition) {
      return false;
    }
    
    return true;
  };

  // Wrapped measurement click handler
  const wrappedMeasurementClick = (position, screenPosition) => {
    if (isMeasurementAllowedAtPosition(screenPosition)) {
      handleMeasurementClick(position);
    }
  };

  // Wrapped measurement right click handler
  const wrappedMeasurementRightClick = (position, screenPosition) => {
    if (!screenPosition || isMeasurementAllowedAtPosition(screenPosition)) {
      handleMeasurementRightClick(position);
    }
  };

  // Show/hide pyramids based on viewpoints panel state
  useEffect(() => {
    if (viewpointsPanelOpen) {
      showPyramids();
    } else {
      hidePyramids();
      clearPyramidHighlight();
    }
  }, [viewpointsPanelOpen, showPyramids, hidePyramids, clearPyramidHighlight]);

  // Show/hide annotations based on annotations panel state
  useEffect(() => {
    if (annotationsPanelOpen) {
      showAnnotations();
    } else {
      hideAnnotations();
      setAnnotationMode(false);
    }
  }, [annotationsPanelOpen, showAnnotations, hideAnnotations, setAnnotationMode]);

  // Update annotation entities when annotations change (for newly added annotations)
  useEffect(() => {
    if (annotationsPanelOpen) {
      showAnnotations();
    }
  }, [annotations, annotationsPanelOpen, showAnnotations]);

  // Initialize SmartZoomController when viewer is ready
  useEffect(() => {
    if (viewer) {
      smartZoomControllerRef.current = new SmartZoomController(viewer);
      // Use main point cloud tileset as target if available
      if (tileset && tileset.boundingSphere) {
        smartZoomControllerRef.current.setTarget(
          tileset.boundingSphere.center,
          tileset.boundingSphere.radius
        );
      }
    }
  }, [viewer, tileset]);

  // No tileset cleanup here; managed inside ModelsTools

  // Update pyramids when viewpoints change (for newly added viewpoints)
  useEffect(() => {
    if (viewpointsPanelOpen) {
      showPyramids();
    }
  }, [viewpoints, viewpointsPanelOpen, showPyramids]);

  // Profiler cleanup is handled inside ProfilerTools component
  

  // Cursor styling for annotation mode
  useEffect(() => {
    if (!containerRef.current) return;
    if (annotationMode) {
      containerRef.current.style.cursor = 'crosshair';
      return () => {
        if (containerRef.current) {
          containerRef.current.style.cursor = '';
        }
      };
    }
  }, [annotationMode]);

  // Add mouse move handler for cursor styling when measurement tool is selected
  useEffect(() => {
    if (!containerRef.current || (measurementMode === MODES.NONE && !annotationMode)) {
      // Reset cursor when no measurement tool is selected
      if (containerRef.current && !annotationMode) {
        containerRef.current.style.cursor = '';
      }
      return;
    }

    const handleMouseMove = (event) => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeX = (event.clientX - containerRect.left) / containerRect.width;
      
      let cursor = 'crosshair'; // Default cursor for measurements
      
      // Check if cursor should be disabled (only when measurement tool is active)
      if (currentViewMode === 'full' && currentDisplayMode === 'gaussian') {
        cursor = 'not-allowed';
      } else if (currentViewMode === 'split' && relativeX > splitPosition) {
        cursor = 'not-allowed';
      }
      
      containerRef.current.style.cursor = cursor;
    };

    const container = containerRef.current;
    container.addEventListener('mousemove', handleMouseMove);
    
    // Set initial cursor when measurement tool is selected
    if (currentViewMode === 'full' && currentDisplayMode === 'gaussian') {
      container.style.cursor = 'not-allowed';
    } else {
      container.style.cursor = 'crosshair';
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.style.cursor = '';
      }
    };
  }, [measurementMode, currentViewMode, currentDisplayMode, splitPosition, MODES.NONE]);

  // Base layer selection state handled inside LayersTools

  // Handlers
  const handleMeasurementToggle = (mode) => {
    if (measurementMode === mode) {
      setMode(MODES.NONE);
    } else {
      setMode(mode);
    }
  };

  const handleExitViewer = () => {
    if (isPublic) {
      navigate('/public/thank-you');
    } else if (adminMode) {
      navigate('/admin');
    } else {
      navigate('/');
    }
  };

  // Wrapped export measurements with toast
  const handleExportMeasurements = useCallback(() => {
    if (!completedMeasurements || completedMeasurements.length === 0) {
      enqueueSnackbar(t('NoMeasurementsToExport'), { variant: 'info' });
      return;
    }
    exportMeasurements();
    enqueueSnackbar(t('MeasurementExported'), { variant: 'success' });
  }, [completedMeasurements, exportMeasurements, enqueueSnackbar, t]);

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await importMeasurements(file);
        enqueueSnackbar(t('MeasurementsImported'), { variant: 'success' });
        event.target.value = '';
      } catch (error) {
        console.error('Failed to import measurements:', error);
        enqueueSnackbar(t('MeasurementImportError'), { variant: 'error' });
      }
    }
  };

  useEffect(() => {
    if (viewer && editingPopupId) {
      cylinderEditControllerRef.current = createCylinderEditController(viewer, {
        getEntityByPopupId,
        computeAxisFromOrientation,
        applyPopupUpdates,
        setEditingPopupId
      });
      cylinderEditControllerRef.current.activate(editingPopupId);
    }

    return () => {
      if (cylinderEditControllerRef.current) {
        cylinderEditControllerRef.current.destroy();
        cylinderEditControllerRef.current = null;
      }
    };
  }, [viewer, editingPopupId, getEntityByPopupId, computeAxisFromOrientation, applyPopupUpdates]);

  // Set up click handlers for measurements
  useEffect(() => {
    if (measurementMode !== MODES.NONE) {
      setupClickHandler(wrappedMeasurementClick, wrappedMeasurementRightClick);
    } 
  }, [measurementMode, setupClickHandler, handleMeasurementClick, handleMeasurementRightClick , MODES.NONE, currentViewMode, currentDisplayMode, splitPosition]);

  // Pending annotation state for custom prompt dialog
  const [pendingAnnotation, setPendingAnnotation] = useState(null);

  // Click handler for annotation placement
  useEffect(() => {
    if (annotationMode && measurementMode === MODES.NONE) {
      const handleAnnotationClick = (position) => {
        const cameraState = getCurrentCameraState();
        setPendingAnnotation({ position, cameraState });
      };
      setupClickHandler(handleAnnotationClick);
    }
  }, [annotationMode, measurementMode, setupClickHandler, getCurrentCameraState, MODES.NONE]);

  // Default click handler when no measurement tool or annotation mode is active (tree popups)
  useEffect(() => {
    if (measurementMode === MODES.NONE && !annotationMode) {
      setupClickHandler(handleTreeEntityClick);
    }
  }, [measurementMode, annotationMode, setupClickHandler, handleTreeEntityClick, MODES.NONE]);

  // Keep tree popup screen positions in sync with camera moves
  useEffect(() => {
    if (!viewer || viewer.isDestroyed?.()) return;

    const update = () => {
      if (treePopupsRef.current.length === 0) return;
      updateTreePopupScreens();
    };

    viewer.scene?.postRender.addEventListener(update);
    return () => {
      if (!viewer || viewer.isDestroyed?.()) return;
      viewer.scene?.postRender.removeEventListener(update);
    };
  }, [viewer, updateTreePopupScreens]);

  const handleViewpointsFileImport = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await importViewpoints(file);
        event.target.value = '';
      } catch (error) {
        console.error('Failed to import viewpoints:', error);
        enqueueSnackbar(error.message || 'Import failed', { variant: 'error' });
      }
    }
  };

  const handleAnnotationsFileImport = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await importAnnotations(file);
        event.target.value = '';
      } catch (error) {
        console.error('Failed to import annotations:', error);
        enqueueSnackbar(error.message || 'Import failed', { variant: 'error' });
      }
    }
  };


  // Camera zoom and positioning handlers with smooth animations
  const handleZoomIn = useCallback(() => {
    if (!viewer || !viewer.camera || !smartZoomControllerRef.current) return;
    
    try {
      // Use SmartZoomController's zoom method directly (handles animation internally)
      smartZoomControllerRef.current.zoom(true);
    } catch (error) {
      console.warn('Error with smart zoom in:', error);
      
      // Fallback to original zoom behavior
      const currentHeight = viewer.camera.positionCartographic.height;
      const zoomFactor = Math.max(currentHeight, 100);
      const currentPosition = viewer.camera.position.clone();
      const currentDirection = viewer.camera.direction.clone();
      const forwardVector = Cesium.Cartesian3.multiplyByScalar(currentDirection, zoomFactor, new Cesium.Cartesian3());
      const newPosition = Cesium.Cartesian3.add(currentPosition, forwardVector, new Cesium.Cartesian3());
      
      viewer.camera.flyTo({
        destination: newPosition,
        orientation: { heading: viewer.camera.heading, pitch: viewer.camera.pitch, roll: viewer.camera.roll },
        duration: 1,
        easingFunction: Cesium.EasingFunction.QUARTIC_IN_OUT
      });
    }
  }, [viewer]);

  const handleZoomOut = useCallback(() => {
    if (!viewer || !viewer.camera || !smartZoomControllerRef.current) return;
    
    try {
      // Use SmartZoomController's zoom method directly (handles animation internally)
      smartZoomControllerRef.current.zoom(false);
    } catch (error) {
      console.warn('Error with smart zoom out:', error);
      
      // Fallback to original zoom behavior
      const currentHeight = viewer.camera.positionCartographic.height;
      const zoomFactor = Math.max(currentHeight * 2, 100);
      const currentPosition = viewer.camera.position.clone();
      const currentDirection = viewer.camera.direction.clone();
      const backwardVector = Cesium.Cartesian3.multiplyByScalar(currentDirection, -zoomFactor, new Cesium.Cartesian3());
      const newPosition = Cesium.Cartesian3.add(currentPosition, backwardVector, new Cesium.Cartesian3());
      
      viewer.camera.flyTo({
        destination: newPosition,
        orientation: { heading: viewer.camera.heading, pitch: viewer.camera.pitch, roll: viewer.camera.roll },
        duration: 1,
        easingFunction: Cesium.EasingFunction.QUARTIC_IN_OUT
      });
    }
  }, [viewer]);

  const handleRecenterCamera = useCallback(() => {
    if (!viewer || !viewer.camera) return;

    try {
      // Use the existing recenterCamera function from useCesiumViewer
      recenterCamera();
      console.log('Camera recentered');
    } catch (error) {
      console.warn('Error recentering camera:', error);
    }
  }, [viewer, recenterCamera]);

  const handleFlyToAxisView = useCallback((heading, pitch) => {
    const target = tileset || gaussianTileset;
    if (!viewer || !target) return;
    try {
      viewer.flyTo(target, {
        duration: 1.2,
        offset: new Cesium.HeadingPitchRange(heading, pitch, 0),
      });
    } catch (error) {
      console.warn('Error flying to axis view:', error);
    }
  }, [viewer, tileset, gaussianTileset]);

  // Toolbar panel toggle handler
  const handleTogglePanel = useCallback((panelName) => {
    togglePanel(panelName);
  }, [togglePanel]);

  // Panel states object for ViewerToolbar
  const panelStates = useMemo(() => ({
    measurement: measurementPanelOpen,
    gaussian: gaussianPanelOpen,
    models3D: modelsPanel3DOpen,
    viewpoints: viewpointsPanelOpen,
    settings: settingsPanelOpen,
    baseLayers: baseLayerPanelOpen,
    clippingPlane: clippingPlanePanelOpen,
    profiler: profilerPanelOpen,
    annotations: annotationsPanelOpen,
    transformation: transformationPanelOpen,
  }), [measurementPanelOpen, gaussianPanelOpen, modelsPanel3DOpen, viewpointsPanelOpen, settingsPanelOpen, baseLayerPanelOpen, clippingPlanePanelOpen, profilerPanelOpen, annotationsPanelOpen, transformationPanelOpen]);

  // Viewer settings context value — consumed by SettingsTools, ColorLegend, etc.
  const viewerSettingsValue = useMemo(() => ({
    // UI settings (from useViewerSettings)
    pointSize, setPointSize,
    useBrowserRecommendationResolution, setUseBrowserRecommendationResolution,
    eyeDomeLighting, setEyeDomeLighting,
    eyeDomeLightingStrength, setEyeDomeLightingStrength,
    eyeDomeLightingRadius, setEyeDomeLightingRadius,
    globeLighting, setGlobeLighting,
    atmosphereRendering, setAtmosphereRendering,
    terrainRendering, setTerrainRendering,
    contourLinesEnabled, setContourLinesEnabled,
    contourSpacing, setContourSpacing,
    thickLineInterval, setThickLineInterval,
    lineThickness, setLineThickness,
    fieldOfView, setFieldOfView,
    zoomFactor, setZoomFactor,
    visualFidelity, setVisualFidelity,
    ssaoEnabled, setSSAOEnabled,
    ssaoIntensity, setSSAOIntensity,
    fxaaEnabled, setFXAAEnabled,
    // Property visualization (from useCesiumViewer)
    selectedProperty,
    updateSelectedProperty,
    classificationFilter,
    updateClassificationFilter,
    intensityRange,
    updateIntensityRange,
    elevationRange,
    updateElevationRange,
    distanceRange,
    updateDistanceRange,
    intensityColormap,
    updateIntensityColormap,
    elevationColormap,
    updateElevationColormap,
    distanceColormap,
    updateDistanceColormap,
    initialElevationMin: elevationBounds?.[0] ?? 0,
    initialElevationMax: elevationBounds?.[1] ?? 100,
    distanceBounds,
  }), [
    pointSize, setPointSize,
    useBrowserRecommendationResolution, setUseBrowserRecommendationResolution,
    eyeDomeLighting, setEyeDomeLighting,
    eyeDomeLightingStrength, setEyeDomeLightingStrength,
    eyeDomeLightingRadius, setEyeDomeLightingRadius,
    globeLighting, setGlobeLighting,
    atmosphereRendering, setAtmosphereRendering,
    terrainRendering, setTerrainRendering,
    contourLinesEnabled, setContourLinesEnabled,
    contourSpacing, setContourSpacing,
    thickLineInterval, setThickLineInterval,
    lineThickness, setLineThickness,
    fieldOfView, setFieldOfView,
    zoomFactor, setZoomFactor,
    visualFidelity, setVisualFidelity,
    ssaoEnabled, setSSAOEnabled,
    ssaoIntensity, setSSAOIntensity,
    fxaaEnabled, setFXAAEnabled,
    selectedProperty, updateSelectedProperty,
    classificationFilter, updateClassificationFilter,
    intensityRange, updateIntensityRange,
    elevationRange, updateElevationRange,
    distanceRange, updateDistanceRange,
    intensityColormap, updateIntensityColormap,
    elevationColormap, updateElevationColormap,
    distanceColormap, updateDistanceColormap,
    elevationBounds, distanceBounds,
  ]);

  return (
    <ViewerSettingsProvider value={viewerSettingsValue}>
    <Box sx={{ position: 'relative', width: '100%', height: '100dvh' }}>
      <ViewerLoadingScreen visible={!datasetLoaded} />

      {/* Main Page Tip */}
      <TipsWindow
        isOpen={isMainTipOpen}
        title={mainTipTitle}
        markdownBody={mainTipBody}
        onClose={({ doNotShowAgain }) => closeMainTip(doNotShowAgain)}
      />

      {/* Annotation text input dialog */}
      <AnnotationInputDialog
        open={!!pendingAnnotation}
        onSubmit={(text) => {
          addAnnotation(pendingAnnotation.position, text, pendingAnnotation.cameraState);
          setPendingAnnotation(null);
        }}
        onCancel={() => setPendingAnnotation(null)}
        t={t}
      />

      {/* Toolbar */}
      <ViewerToolbar
        hasGaussianOnly={hasGaussianOnly}
        gaussianSplattingId={gaussianSplattingId}
        resultsAssetId={resultsAssetId}
        panelStates={panelStates}
        onTogglePanel={handleTogglePanel}
        onExit={handleExitViewer}
        showTransformation={!hasGeoJsonResult}
      />

      {/* Measurement Panel */}
      {!hasGaussianOnly && measurementPanelOpen && (
        <ToolPanel
          title={t('MeasurementTools')}
          onClose={toggleMeasurementPanel}
          width={DRAWER_WIDTH}
        >
          <MeasurementTools
            MODES={MODES}
            measurementMode={measurementMode}
            measurementPoints={measurementPoints}
            completedMeasurements={completedMeasurements}
            highlightedMeasurement={highlightedMeasurement}
            handleMeasurementToggle={handleMeasurementToggle}
            clearMeasurements={clearMeasurements}
            exportMeasurements={handleExportMeasurements}
            handleFileImport={handleFileImport}
            deleteMeasurement={deleteMeasurement}
            toggleMeasurementVisibility={toggleMeasurementVisibility}
            highlightMeasurement={highlightMeasurement}
            clearMeasurementHighlight={clearMeasurementHighlight}
          />
        </ToolPanel>
      )}
      {/* Gaussian Splatting Panel - only show if gaussianSplattingId exists */}
      {!hasGaussianOnly && gaussianPanelOpen && gaussianSplattingId && (
        <GaussianSplattingTools
          currentViewMode={currentViewMode}
          currentDisplayMode={currentDisplayMode}
          switchViewMode={switchViewMode}
          switchDisplayMode={switchDisplayMode}
          toggleGaussianPanel={toggleGaussianPanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
        />
      )}

      {/* Profiler Panel */}
      {!hasGaussianOnly && profilerPanelOpen && (
        <ToolPanel
          title={t('Profiler')}
          onClose={toggleProfilerPanel}
          width={DRAWER_WIDTH}
        >
          <ProfilerTools
            viewer={viewer}
            tileset={tileset}
            updateProfilerHighlighting={updateProfilerHighlighting}
            profileLoadingId={profileLoadingId}
            onOpenPreview={(profile) => {
              setProfileLoadingId(profile?.id || null);
              setPreviewProfile(profile);
            }}
          />
        </ToolPanel>
      )}

      {/* 3D Models Tools (kept mounted when results are available; shows UI when panel open) */}
      {resultsAssetId && resultsAssetId.length > 0 && (
        <ModelsTools
          viewer={viewer}
          token={token}
          tileset={tileset}
          pointCloudTilesetId={id}
          resultsAssetId={resultsAssetId}
          toggle3DModelsPanel={toggle3DModelsPanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
          panelOpen={modelsPanel3DOpen}
          pointValues={transformPointValues}
          pointGeoRef={transformPointGeoRef}
          onAssetTypesResolved={handleAssetTypesResolved}
        />
      )}

      {/* Viewpoints Panel */}
      {viewpointsPanelOpen && (
        <ViewpointsTools
          viewpoints={viewpoints}
          highlightedViewpoint={highlightedViewpoint}
          saveCurrentViewpoint={saveCurrentViewpoint}
          handleViewpointsFileImport={handleViewpointsFileImport}
          clearAllViewpoints={clearAllViewpoints}
          exportViewpoints={exportViewpoints}
          restoreViewpoint={restoreViewpoint}
          deleteViewpoint={deleteViewpoint}
          highlightPyramid={highlightPyramid}
          clearPyramidHighlight={clearPyramidHighlight}
          toggleViewpointsPanel={toggleViewpointsPanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
        />
      )}

      {/* Annotations Panel */}
      {annotationsPanelOpen && (
        <AnnotationsTools
          annotations={annotations}
          highlightedAnnotation={highlightedAnnotation}
          annotationMode={annotationMode}
          setAnnotationMode={setAnnotationMode}
          deleteAnnotation={deleteAnnotationFn}
          clearAllAnnotations={clearAllAnnotations}
          exportAnnotations={exportAnnotations}
          handleAnnotationsFileImport={handleAnnotationsFileImport}
          flyToAnnotation={flyToAnnotation}
          highlightAnnotation={highlightAnnotation}
          clearHighlight={clearHighlight}
          toggleAnnotationsPanel={toggleAnnotationsPanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
        />
      )}

      {/* Transformation Panel */}
      {transformationPanelOpen && !hasGeoJsonResult && (
        <TransformationTools
          onClose={toggleTransformationPanel}
          tileset={tileset}
          gaussianTileset={gaussianTileset}
          pointValues={transformPointValues}
          gsValues={transformGsValues}
          pointValuesRef={transformPointValuesRef}
          gsValuesRef={transformGsValuesRef}
          applyPoint={transformApplyPoint}
          applyGs={transformApplyGs}
          onSave={isPublic ? null : saveTransformation}
        />
      )}

      {/* Settings Panel */}
      {settingsPanelOpen && (
        <SettingsTools
          toggleSettingsPanel={toggleSettingsPanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
          hasGaussianOnly={hasGaussianOnly}
        />
      )}

      {/* Base Layers Panel */}
      {baseLayerPanelOpen && (
        <LayersTools
          viewer={viewer}
          toggleBaseLayerPanel={toggleBaseLayerPanel}
          imageryProviderViewModels={imageryProviderViewModels}
          terrainProviderViewModels={terrainProviderViewModels}
          DRAWER_WIDTH={DRAWER_WIDTH}
        />
      )}

      {/* Clipping Plane Tools (kept mounted; shows UI when panel open) */}
      {!hasGaussianOnly && (
        <ClippingPlaneTools
          viewer={viewer}
          tileset={tileset}
          panelOpen={clippingPlanePanelOpen}
          onClosePanel={toggleClippingPlanePanel}
          DRAWER_WIDTH={DRAWER_WIDTH}
        />
      )}

      {/* Minimap Panel */}
      {minimapOpen && viewer && (
        <MinimapViewer
          mainViewer={viewer}
          tileset={tileset || gaussianTileset}
          assetId={id || gaussianSplattingId}
          onClose={handleToggleMinimap}
          selectedProperty={selectedProperty}
          intensityRange={intensityRange}
        />
      )}

      {/* Navigation Cluster (Compass + Zoom + Minimap toggle) */}
      <NavigationCluster
        viewer={viewer}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onRecenter={handleRecenterCamera}
        onFlyToAxisView={handleFlyToAxisView}
        terrainEnabled={terrainRendering}
        minimapOpen={minimapOpen}
        onToggleMinimap={handleToggleMinimap}
        isWalkMode={isWalkMode}
        onToggleWalkMode={toggleWalkMode}
      />

      {/* Split Screen Divider - only show when in split mode */}
      {currentViewMode === 'split' && (
        <SplitScreenDivider
          splitPosition={splitPosition}
          onSplitPositionChange={updateSplitPosition}
          containerRef={containerRef}
        />
      )}

      {/* Color Legend */}
      <ColorLegend statusBarVisible />

      {/* Status Bar */}
      <StatusBar viewer={viewer} hasGaussianOnly={hasGaussianOnly} />

      {/* Floating Profile Preview Window */}
      <FloatingProfilePreview
        open={!!previewProfile}
        profile={previewProfile}
        token={token}
        tilesetId={id}
        onClose={() => { setProfileLoadingId(null); setPreviewProfile(null); }}
        onSetProfileLoadingId={setProfileLoadingId}
        t={t}
      />

      <TreePopups
        popups={treePopupViewModels}
        trackedPopupId={trackedPopupId}
        t={t}
        onToggleTracked={toggleTrackedPopup}
        onDeleteTree={deleteTreePopupAndEntity}
        onToggleEditMode={togglePopupEditMode}
        onClosePopup={closeTreePopup}
        onFieldChange={handleEditFieldChange}
        onRadiusStep={handleRadiusStep}
      />
      
      {/* Walk mode HUD overlay */}
      {isWalkMode && (
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1300 }}>
          {/* Crosshair */}
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: 20, height: 20 }}>
            <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px',
              bgcolor: 'rgba(255,255,255,0.7)' }} />
            <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px',
              bgcolor: 'rgba(255,255,255,0.7)' }} />
          </Box>
          {/* Hint text */}
          <Typography variant="caption" sx={{ position: 'absolute', bottom: 80, left: '50%',
            transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.6)', userSelect: 'none' }}>
            {t('WalkModeHint')}
          </Typography>
        </Box>
      )}

      {/* Cesium Canvas */}
      <div ref={containerRef} style={{ width: '100%', height: '100%'}} />
    </Box>
    </ViewerSettingsProvider>
  );
}

BaseCesiumViewer.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string.isRequired,
  gaussianSplattingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resultsAssetId: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])),
  isPublic: PropTypes.bool
};

export default BaseCesiumViewer;
